import { Data, Effect, Result, Option } from 'effect';

import { paginationPageParamName } from '@vs-shop/engine/config/pagination';
import * as Url from 'effect/unstable/http/Url';
import * as UrlParams from 'effect/unstable/http/UrlParams';

class PaginationUrlError extends Data.TaggedError('PaginationUrlError')<{
  readonly message: string;
  readonly field: string;
  readonly value: unknown;
}> {}

type PaginationUrlParams = {
  readonly baseUrl: string;
  readonly pageNumber: number;
  readonly pageParamName?: string;
};

/**
 * Validates and normalizes pagination URL parameters with simple checks
 */
const validatePaginationParams = (params: PaginationUrlParams) =>
  Effect.gen(function* () {
    const baseUrl = params.baseUrl || '/';

    if (params.pageNumber < 1 || !Number.isInteger(params.pageNumber)) {
      return yield* new PaginationUrlError({
        message: 'pageNumber must be a positive integer',
        field: 'pageNumber',
        value: params.pageNumber,
      });
    }

    return {
      ...params,
      baseUrl,
    };
  });

/**
 * Creates a canonical URL with optional page parameter
 * Page 1 returns the base URL without page parameter
 * Page 2+ returns the URL with page parameter
 */
export const createCanonicalUrlWithPage = (params: PaginationUrlParams) =>
  Effect.gen(function* () {
    const validated = yield* validatePaginationParams(params);

    const { baseUrl, pageNumber, pageParamName = paginationPageParamName } = validated;

    if (pageNumber === 1) {
      return baseUrl;
    }

    const urlResult = Url.fromString(baseUrl, 'http://localhost');
    const url = yield* Result.isSuccess(urlResult)
      ? Effect.succeed(urlResult.success)
      : Effect.fail(urlResult.failure);

    const updatedUrl = Url.modifyUrlParams(url, (urlParams) =>
      UrlParams.set(urlParams, pageParamName, pageNumber)
    );

    return updatedUrl.pathname + updatedUrl.search;
  });

/**
 * Creates previous page URL or Option.none() if no previous page exists
 */
export const createPreviousPageUrl = (params: PaginationUrlParams) =>
  Effect.gen(function* () {
    const validated = yield* validatePaginationParams(params);

    const { baseUrl, pageNumber } = validated;

    if (pageNumber <= 1) {
      return Option.none<string>();
    }

    const prevPageNumber = pageNumber - 1;

    const prevUrl = yield* createCanonicalUrlWithPage({
      baseUrl,
      pageNumber: prevPageNumber,
    });

    return Option.some(prevUrl);
  });

/**
 * Creates next page URL with proper page parameter
 */
export const createNextPageUrl = (params: PaginationUrlParams & { hasNext: boolean }) =>
  Effect.gen(function* () {
    const validated = yield* validatePaginationParams(params);

    const { baseUrl, pageNumber } = validated;
    const { hasNext } = params;

    if (!hasNext) {
      return Option.none<string>();
    }

    const nextPageNumber = pageNumber + 1;

    const nextUrl = yield* createCanonicalUrlWithPage({
      baseUrl,
      pageNumber: nextPageNumber,
    });

    return Option.some(nextUrl);
  });

/**
 * Complete pagination URL generation for metadata
 */
export const generatePaginationUrls = (params: {
  baseUrl: string;
  currentPage: number;
  hasNext: boolean;
}) =>
  Effect.gen(function* () {
    const { baseUrl, currentPage, hasNext } = params;

    const canonical = yield* createCanonicalUrlWithPage({
      baseUrl,
      pageNumber: currentPage,
    });

    const previous = yield* createPreviousPageUrl({
      baseUrl,
      pageNumber: currentPage,
    });

    const next = yield* createNextPageUrl({
      baseUrl,
      pageNumber: currentPage,
      hasNext,
    });

    return {
      canonical,
      previous,
      next,
    };
  });

/**
 * Simple synchronous pagination URL creation for client and server
 * Reuses same logic as server-side metadata generation but without Effect wrapper
 *
 * @param baseUrl - Base URL path (e.g., '/kategorie/electronics' or taxon.routerUrl)
 * @param pageNumber - Target page number (1-indexed)
 * @param pageParamName - Name of the page query parameter (defaults to config value)
 * @returns URL string with page parameter for page > 1, base URL for page 1
 *
 * @example
 * createPaginationUrlClient('/kategorie/electronics', 1) // Returns: '/kategorie/electronics'
 * createPaginationUrlClient('/kategorie/electronics', 2) // Returns: '/kategorie/electronics?page=2'
 * createPaginationUrlClient('/brands?alphabet=A', 1) // Returns: '/brands?alphabet=A'
 * createPaginationUrlClient('/brands?alphabet=A', 2) // Returns: '/brands?alphabet=A&page=2'
 * createPaginationUrlClient('/items', 2, 'p') // Returns: '/items?p=2'
 */
export function createPaginationUrlClient(
  baseUrl: string,
  pageNumber: number,
  pageParamName: string = paginationPageParamName
): string {
  try {
    const url = new URL(baseUrl, 'http://localhost');

    if (pageNumber === 1) {
      // Remove page param for page 1, preserve other params
      url.searchParams.delete(pageParamName);
    } else {
      url.searchParams.set(pageParamName, String(pageNumber));
    }

    return url.pathname + url.search;
  } catch {
    return baseUrl;
  }
}

export const getFullUrl = (
  baseUrl: string,
  page: number,
  searchParams: Record<string, string>,
  pageParamName: string = paginationPageParamName
) => {
  const url = new URL(baseUrl, 'http://localhost');
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  const urlWithParams = url.pathname + url.search;
  return createPaginationUrlClient(urlWithParams, page, pageParamName);
};
